import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { filter, map, mergeMap } from 'rxjs/operators';

const SITE_URL = 'https://alreadydone.us';
const SITE_NAME = 'Already Done';
const DEFAULT_TITLE = `${SITE_NAME} | Home Services Marketplace — Find Trusted Contractors`;
const DEFAULT_DESCRIPTION = 'Already Done connects homeowners with vetted electricians, plumbers, carpenters, and general contractors. Browse services, request a bid, and book trusted local pros.';

@Injectable({ providedIn: 'root' })
export class SeoService {
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private title: Title,
    private meta: Meta
  ) {}

  init(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => {
        let route = this.activatedRoute;
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      mergeMap(route => route.data)
    ).subscribe(data => {
      const pageTitle = data['title'] ? `${data['title']} | ${SITE_NAME}` : DEFAULT_TITLE;
      const description = data['description'] || DEFAULT_DESCRIPTION;
      // Hash routing means individual routes aren't independently crawlable/indexable,
      // so every page canonicalizes to the root — this is a real "sharable" URL, not
      // an SEO-indexed one, since everything after the # never reaches the server.
      const shareableUrl = SITE_URL + '/#' + this.router.url.split('?')[0];

      this.title.setTitle(pageTitle);
      this.meta.updateTag({ name: 'description', content: description });
      this.meta.updateTag({ property: 'og:title', content: pageTitle });
      this.meta.updateTag({ property: 'og:description', content: description });
      this.meta.updateTag({ property: 'og:url', content: shareableUrl });
      this.updateCanonicalUrl(SITE_URL + '/');
    });
  }

  private updateCanonicalUrl(url: string): void {
    let link: HTMLLinkElement | null = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }
}
